import React, {
  ComponentProps,
  ReactNode,
  memo,
  useEffect,
  useState,
} from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { Box } from './Box'
import { FlexBox } from './FlexBox'
import { TextButton } from './TextButton'

const ANIMATION_DURATION_MS = 300
const ANIMATION_DURATION = `${ANIMATION_DURATION_MS}ms`

const ListItemWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    !['showGradient', 'hideBorderBottom'].includes(prop),
})<{ showGradient?: boolean; hideBorderBottom?: boolean }>`
  position: relative;
  border-bottom: ${({ theme, hideBorderBottom }) =>
    hideBorderBottom
      ? 'none'
      : `${theme.ui.spacings.xxxs} solid ${theme.ui.colors['navy.10']}`};
  padding-block: ${({ theme }) => theme.ui.spacings.sm};
  padding-inline: ${({ theme }) => theme.ui.spacings.md};

  ${({ showGradient }) =>
    showGradient &&
    css`
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 100%;
        background: linear-gradient(
          to bottom,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 1) 100%
        );
        pointer-events: none;
      }
    `}
`

const Container = styled(FlexBox)`
  border: ${({ theme }) => theme.ui.spacings.xxxs} solid
    ${({ theme }) => theme.ui.colors['navy.10']};
  border-radius: ${({ theme }) => theme.ui.spacings.sm};
  overflow: hidden;
`

const HiddenItemsContainer = styled(Box, {
  shouldForwardProp: (prop) => !['isExpanded'].includes(prop),
})<{ isExpanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ isExpanded }) => (isExpanded ? '1fr' : '0fr')};
  transition: grid-template-rows ${ANIMATION_DURATION} ease-in-out;
`

const HiddenItemsContent = styled(Box)`
  min-height: 0;
  overflow: hidden;
`

interface ExpandableListProps extends ComponentProps<typeof FlexBox> {
  visibleItemsCount: number
  items: ReactNode[]
  expandLabel: string
  collapseLabel: string
  onExpandToggle?: (isExpanded: boolean) => void
}

export const ExpandableList = memo(
  ({
    visibleItemsCount,
    items,
    expandLabel,
    collapseLabel,
    onExpandToggle,
    ...rest
  }: ExpandableListProps) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const [showGradient, setShowGradient] = useState(true)

    const hasHiddenItems = items.length > visibleItemsCount
    const visibleItems = items.slice(0, visibleItemsCount)
    const hiddenItems = items.slice(visibleItemsCount)

    useEffect(() => {
      if (isExpanded) {
        setShowGradient(false)
      } else {
        const timeout = setTimeout(() => {
          setShowGradient(true)
        }, ANIMATION_DURATION_MS)

        return () => clearTimeout(timeout)
      }
    }, [isExpanded])

    const handleToggle = () => {
      const newExpandedState = !isExpanded
      setIsExpanded(newExpandedState)
      onExpandToggle?.(newExpandedState)
    }

    return (
      <Container {...{ fd: 'column', bgc: 'white.100', ...rest }}>
        {visibleItems.map((item, index) => {
          const isLastVisibleItem = index === visibleItems.length - 1
          const shouldShowGradient =
            showGradient && hasHiddenItems && isLastVisibleItem
          const hideBorderBottom = isLastVisibleItem && !hasHiddenItems

          return (
            <ListItemWrapper
              {...{
                showGradient: shouldShowGradient,
                hideBorderBottom,
              }}
              key={index}>
              {item}
            </ListItemWrapper>
          )
        })}

        {hasHiddenItems ? (
          <HiddenItemsContainer {...{ isExpanded }}>
            <HiddenItemsContent>
              {hiddenItems.map((item, index) => (
                <ListItemWrapper key={visibleItemsCount + index}>
                  {item}
                </ListItemWrapper>
              ))}
            </HiddenItemsContent>
          </HiddenItemsContainer>
        ) : null}

        {hasHiddenItems ? (
          <FlexBox {...{ jc: 'center', ai: 'center', padding: 'sm' }}>
            <TextButton
              {...{ onClick: handleToggle, variant: 'default', size: 'xs' }}>
              {isExpanded ? collapseLabel : expandLabel}
            </TextButton>
          </FlexBox>
        ) : null}
      </Container>
    )
  },
)
